import { Box } from "@/components/ui";

const PREVIEW_WHITE = "#ffffff";

/** Fixed size for all thumbnails */
const THUMB_WIDTH = 128;
const THUMB_HEIGHT = 80;

/** Document body area — no placeholder lines, just empty space */
function DocBodyLines({ showTitle = false, sx = {} }) {
  return <Box sx={{ flex: 1, minHeight: 0, ...sx }} />;
}

/** Tiny invoice table (2 rows, 2 cols) */
function InvoiceTable({ accentColor = "#1565c0" }) {
  return (
    <Box sx={{ width: "100%", border: "1px solid", borderColor: "grey.300", borderRadius: 0.5, overflow: "hidden" }}>
      <Box sx={{ display: "flex", borderBottom: "1px solid", borderColor: "grey.300" }}>
        <Box sx={{ flex: 1, height: 6, borderRight: "1px solid", borderColor: "grey.300", bgcolor: "grey.100" }} />
        <Box sx={{ flex: 1, height: 6, bgcolor: "grey.100" }} />
      </Box>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flex: 1, height: 5, borderRight: "1px solid", borderColor: "grey.300" }} />
        <Box sx={{ flex: 1, height: 5 }} />
      </Box>
      <Box sx={{ height: 3, bgcolor: accentColor, opacity: 0.2, mt: 0.25 }} />
    </Box>
  );
}

/**
 * Mini visual preview for each document design.
 * Same size for all; each design has a distinct, polished look inside.
 * @param {Object} colors - Optional { primary, secondary, accent, headerBg, textOnHeader } to preview with custom palette
 */
const DEFAULT_PRIMARY = "#1565c0";
const DEFAULT_SECONDARY = "#0d47a1";
const DEFAULT_ACCENT = "#42a5f5";

export default function DocumentDesignThumbnail({ designId, colors: customColors, sx = {} }) {
  const primaryColor = customColors?.primary ?? DEFAULT_PRIMARY;
  const secondaryColor = customColors?.secondary ?? DEFAULT_SECONDARY;
  const accentColor = customColors?.accent ?? DEFAULT_ACCENT;
  const base = {
    width: THUMB_WIDTH,
    height: THUMB_HEIGHT,
    minWidth: THUMB_WIDTH,
    minHeight: THUMB_HEIGHT,
    maxWidth: THUMB_WIDTH,
    maxHeight: THUMB_HEIGHT,
    borderRadius: 1.25,
    overflow: "hidden",
    bgcolor: "#fff",
    border: "1px solid",
    borderColor: "divider",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    ...sx,
  };

  switch (designId) {
    case "minimal":
    case "minimal-ltr":
      return (
        <Box sx={base}>
          <Box sx={{ borderBottom: "3px solid " + primaryColor, pb: 0.6, mb: 0.6, px: 0.75 }}>
            <Box sx={{ height: 2.5, width: 32, bgcolor: accentColor, borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ borderTop: "1.5px solid", borderColor: "grey.300", height: 5, flexShrink: 0 }} />
        </Box>
      );
    case "wave-header":
      return (
        <Box sx={base}>
          <Box sx={{ height: 2, flexShrink: 0, bgcolor: "#1a1a1a" }} />
          <Box sx={{ height: 18, flexShrink: 0, bgcolor: primaryColor, borderRadius: "0 0 50% 50% / 0 0 28% 28%" }} />
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ height: 14, flexShrink: 0, bgcolor: primaryColor, borderRadius: "50% 50% 0 0 / 28% 28% 0 0", opacity: 0.9 }} />
          <Box sx={{ height: 2, flexShrink: 0, bgcolor: "#1a1a1a" }} />
        </Box>
      );
    case "band-left":
      return (
        <Box sx={{ ...base, flexDirection: "row" }}>
          <Box sx={{ width: 7, flexShrink: 0, bgcolor: primaryColor }} />
          <Box sx={{ width: 4, flexShrink: 0, bgcolor: secondaryColor }} />
          <Box sx={{ flex: 1, minWidth: 0, p: 0, display: "flex", flexDirection: "column" }}>
            <Box sx={{ borderRight: "3px solid " + accentColor, pr: 0.5, mb: 0.5 }}>
              <Box sx={{ width: 18, height: 2.5, bgcolor: primaryColor, borderRadius: 0.5 }} />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DocBodyLines showTitle />
            </Box>
          </Box>
        </Box>
      );
    case "band-top":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, bgcolor: primaryColor, color: PREVIEW_WHITE, py: 0.5, px: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ height: 2, width: 14, bgcolor: "rgba(255,255,255,0.6)", borderRadius: 0.5 }} />
            <Box sx={{ height: 1.5, width: 20, bgcolor: "rgba(255,255,255,0.4)", borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, border: "1px solid", borderColor: "grey.300", borderTop: "none", p: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "elegant-frame":
      return (
        <Box sx={{ ...base, p: 0.6 }}>
          <Box sx={{ border: "2px solid " + primaryColor, borderRadius: 0.75, flex: 1, minHeight: 0, position: "relative", p: 0 }}>
            <Box sx={{ position: "absolute", top: 0, right: 0, width: 12, height: 12, borderLeft: "2px solid " + accentColor, borderBottom: "2px solid " + accentColor }} />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, width: 12, height: 12, borderRight: "2px solid " + accentColor, borderTop: "2px solid " + accentColor }} />
            <Box sx={{ height: 2.5, width: "42%", bgcolor: primaryColor, opacity: 0.45, borderRadius: 0.5, mb: 0.5 }} />
            <DocBodyLines />
          </Box>
        </Box>
      );
    case "invoice-style":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, bgcolor: primaryColor, color: PREVIEW_WHITE, py: 0.6, px: 0.75, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ height: 2.5, width: 22, bgcolor: secondaryColor, borderRadius: 0.5 }} />
            <Box sx={{ height: 2.5, width: 12, bgcolor: "rgba(255,255,255,0.55)", borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, border: "1.5px solid", borderColor: "grey.300", borderTop: "none", p: 0, minHeight: 0 }}>
            <InvoiceTable accentColor={primaryColor} />
          </Box>
          <Box sx={{ height: 4, flexShrink: 0, bgcolor: primaryColor }} />
        </Box>
      );
    case "sidebar-contact":
      return (
        <Box sx={{ ...base, flexDirection: "row" }}>
          <Box sx={{ width: "28%", minWidth: 28, flexShrink: 0, bgcolor: "#1a1a1a", display: "flex", flexDirection: "column", alignItems: "center", py: 0.5, gap: 0.5 }}>
            <Box sx={{ width: "70%", height: 2, bgcolor: accentColor }} />
            <Box sx={{ width: "70%", height: 1.5, bgcolor: primaryColor, opacity: 0.8 }} />
            <Box sx={{ width: "70%", height: 1.5, bgcolor: accentColor, opacity: 0.6 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, p: 0 }}>
            <Box sx={{ borderBottom: "2px solid " + accentColor, pb: 0.35, mb: 0.5, width: 22 }} />
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DocBodyLines showTitle />
            </Box>
          </Box>
        </Box>
      );
    case "double-line":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, borderTop: "2px solid " + primaryColor, borderBottom: "1px solid " + secondaryColor, py: 0.25, mb: 0.5, px: 0.75 }}>
            <Box sx={{ height: 2, width: "35%", bgcolor: "grey.600", borderRadius: 0.5, mx: "auto" }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines />
          </Box>
          <Box sx={{ flexShrink: 0, borderTop: "1px solid " + secondaryColor, borderBottom: "2px solid " + primaryColor, py: 0.25 }} />
        </Box>
      );
    case "rounded-corners":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`, height: 6, borderRadius: "10px 10px 0 0", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }} />
          <Box sx={{ flex: 1, mx: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ flexShrink: 0, bgcolor: accentColor, height: 3, borderRadius: "0 0 10px 10px", opacity: 0.9 }} />
        </Box>
      );
    case "geometric":
      return (
        <Box sx={{ ...base, position: "relative" }}>
          <Box sx={{ flexShrink: 0, bgcolor: "#1a1a2e", position: "relative", overflow: "hidden", py: 0.7, px: 0.5 }}>
            <Box sx={{ position: "absolute", top: -4, right: -4, width: 24, height: 24, bgcolor: accentColor, opacity: 0.95, transform: "rotate(15deg)" }} />
            <Box sx={{ position: "absolute", top: 2, left: 2, width: 10, height: 10, border: "1.5px solid " + accentColor, borderRadius: 0.5 }} />
            <Box sx={{ position: "relative", zIndex: 1, height: 2, width: 12, bgcolor: accentColor, borderRadius: 1 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ height: 2, flexShrink: 0, background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})` }} />
        </Box>
      );
    case "clean-box":
      return (
        <Box sx={base}>
          <Box sx={{ border: "1.5px solid " + primaryColor, borderRadius: 0.5, m: 0.5, flex: 1, minHeight: 0, p: 0 }}>
            <Box sx={{ height: 2.5, width: "45%", bgcolor: primaryColor, opacity: 0.5, borderRadius: 0.5, mb: 0.5 }} />
            <DocBodyLines />
          </Box>
        </Box>
      );
    case "bold-header":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, bgcolor: primaryColor, color: PREVIEW_WHITE, py: 0.85, px: 0.75, display: "flex", flexDirection: "column", gap: 0.35 }}>
            <Box sx={{ height: 3, width: 26, bgcolor: "rgba(255,255,255,0.55)", borderRadius: 0.5 }} />
            <Box sx={{ height: 2, width: 20, bgcolor: "rgba(255,255,255,0.38)", borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, p: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "underline-accent":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, borderBottom: "2px solid " + accentColor, pb: 0.25, mb: 0.5, px: 0.75 }}>
            <Box sx={{ height: 2, width: "50%", bgcolor: primaryColor, opacity: 0.6, borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "letterhead":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, borderTop: "2px solid " + primaryColor, borderBottom: "1.5px solid " + secondaryColor, py: 0.4, px: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: 0.5, border: "1.5px solid " + accentColor, flexShrink: 0 }} />
            <Box sx={{ height: 2, width: 28, bgcolor: "grey.400", borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ flexShrink: 0, borderTop: "1.5px solid " + secondaryColor, py: 0.3 }} />
        </Box>
      );
    case "split-header":
      return (
        <Box sx={base}>
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <Box sx={{ flex: 1, bgcolor: primaryColor, py: 0.65, px: 0.6, display: "flex", flexDirection: "column", gap: 0.4, borderRadius: "0 0 0 10px" }}>
              <Box sx={{ height: 2, width: 20, bgcolor: "rgba(255,255,255,0.45)", borderRadius: 1 }} />
              <Box sx={{ height: 1.5, width: 30, bgcolor: "rgba(255,255,255,0.3)", borderRadius: 1 }} />
            </Box>
            <Box sx={{ width: 3, bgcolor: secondaryColor, borderRadius: "0 0 4px 0" }} />
          </Box>
          <Box sx={{ flex: 1, border: "1px solid", borderColor: "grey.200", borderTop: "none", p: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "corner-ribbon":
      return (
        <Box sx={{ ...base, position: "relative" }}>
          <Box sx={{ position: "absolute", top: 0, right: 0, width: 0, height: 0, borderStyle: "solid", borderWidth: "0 20px 20px 0", borderColor: "transparent " + primaryColor + " transparent transparent" }} />
          <Box sx={{ borderRight: "3px solid " + accentColor, pr: 0.5, mb: 0.5, mt: 0.5 }}>
            <Box sx={{ width: 22, height: 2.5, bgcolor: primaryColor, borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "footer-bar":
      return (
        <Box sx={base}>
          <Box sx={{ borderBottom: "3px solid " + primaryColor, pb: 0.5, mb: 0.5, px: 0.75 }}>
            <Box sx={{ height: 2.5, width: 18, bgcolor: accentColor, borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ flexShrink: 0, bgcolor: secondaryColor, height: 6, mt: 0.5 }} />
        </Box>
      );
    case "minimal-dots":
      return (
        <Box sx={base}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, px: 0.75, pt: 0.5 }}>
            <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: primaryColor }} />
            <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: accentColor }} />
            <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: primaryColor, opacity: 0.5 }} />
            <Box sx={{ flex: 1, height: 1, bgcolor: "grey.300", borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ borderTop: "1.5px solid", borderColor: "grey.300", mt: 0.5, flexShrink: 0 }} />
        </Box>
      );
    case "two-tone":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, height: 22, bgcolor: "#1a1a2e", position: "relative", p: 0.6 }}>
            <Box sx={{ position: "absolute", top: 0, right: 0, width: "50%", height: "100%", background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, opacity: 0.9 }} />
            <Box sx={{ position: "relative", zIndex: 1, height: 2, width: 14, bgcolor: accentColor, borderRadius: 1 }} />
            <Box sx={{ position: "relative", zIndex: 1, height: 1.5, width: 24, bgcolor: "rgba(255,255,255,0.5)", borderRadius: 1, mt: 0.4 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "sidebar-right":
      return (
        <Box sx={{ ...base, flexDirection: "row" }}>
          <Box sx={{ flex: 1, minWidth: 0, p: 0, display: "flex", flexDirection: "column" }}>
            <Box sx={{ borderRight: "3px solid " + accentColor, pr: 0.5, mb: 0.5 }}>
              <Box sx={{ width: 16, height: 2, bgcolor: primaryColor, borderRadius: 0.5 }} />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DocBodyLines showTitle />
            </Box>
          </Box>
          <Box sx={{ width: 3, flexShrink: 0, bgcolor: secondaryColor }} />
          <Box sx={{ width: 6, flexShrink: 0, bgcolor: primaryColor }} />
        </Box>
      );
    case "glass-header":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, height: 24, background: `linear-gradient(160deg, ${primaryColor} 0%, ${accentColor} 100%)`, position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", p: 0.6, boxShadow: "0 4px 14px rgba(0,0,0,0.08)" }}>
            <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "linear-gradient(120deg, rgba(255,255,255,0.15) 0%, transparent 50%)" }} />
            <Box sx={{ position: "relative", zIndex: 1, height: 2, width: 14, bgcolor: "rgba(255,255,255,0.6)", borderRadius: 1 }} />
            <Box sx={{ position: "relative", zIndex: 1, height: 1.5, width: 26, bgcolor: "rgba(255,255,255,0.35)", borderRadius: 1, mt: 0.35 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "edge-accent":
      return (
        <Box sx={{ ...base, flexDirection: "row" }}>
          <Box sx={{ width: 6, flexShrink: 0, bgcolor: "#1a1a1a" }} />
          <Box sx={{ width: 2, flexShrink: 0, background: `linear-gradient(180deg, ${primaryColor}, ${accentColor})` }} />
          <Box sx={{ flex: 1, minWidth: 0, p: 0 }}>
            <Box sx={{ borderBottom: "2px solid " + accentColor, pb: 0.3, mb: 0.5, width: 18 }} />
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DocBodyLines showTitle />
            </Box>
          </Box>
        </Box>
      );
    case "minimal-line":
      return (
        <Box sx={base}>
          <Box sx={{ height: 2, background: `linear-gradient(90deg, transparent, ${primaryColor}, ${accentColor}, transparent)`, mb: 0.5 }} />
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ height: 1, bgcolor: "grey.300", mt: 0.5, flexShrink: 0 }} />
        </Box>
      );
    case "card-modern":
      return (
        <Box sx={base}>
          <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1.5, flex: 1, minHeight: 0, m: 0.5, p: 0, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <Box sx={{ height: 3, bgcolor: primaryColor, borderRadius: "12px 12px 0 0" }} />
            <Box sx={{ height: 6, bgcolor: primaryColor, borderRadius: "0 0 50% 50% / 0 0 24% 24%", marginTop: -1.5, opacity: 0.85 }} />
            <DocBodyLines showTitle />
            <Box sx={{ height: 2.5, bgcolor: accentColor, borderRadius: "0 0 12px 12px", mt: 0.5, opacity: 0.8 }} />
          </Box>
        </Box>
      );
    case "minimal-divider":
    case "minimal-subtle":
      return (
        <Box sx={base}>
          <Box sx={{ borderBottom: "2px solid " + primaryColor, opacity: 0.5, pb: 0.5, mb: 0.5 }} />
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "modern-flat":
      return (
        <Box sx={base}>
          <Box sx={{ height: 1.5, flexShrink: 0, bgcolor: primaryColor }} />
          <Box sx={{ flexShrink: 0, py: 0.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.35 }}>
            <Box sx={{ height: 1.5, width: 14, bgcolor: accentColor, borderRadius: 1 }} />
            <Box sx={{ height: 1, width: 28, bgcolor: "grey.300", borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ height: 10, flexShrink: 0, bgcolor: primaryColor, borderRadius: "50% 50% 0 0 / 20% 20% 0 0", opacity: 0.9 }} />
        </Box>
      );
    case "modern-soft":
      return (
        <Box sx={base}>
          <Box sx={{ height: 5, background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`, borderRadius: "0 0 50% 50% / 0 0 18% 18%", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }} />
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ height: 12, background: `linear-gradient(90deg, ${accentColor}, ${primaryColor})`, borderRadius: "50% 50% 0 0 / 22% 22% 0 0", opacity: 0.88 }} />
        </Box>
      );
    case "corporate-strip":
      return (
        <Box sx={base}>
          <Box sx={{ flexShrink: 0, bgcolor: primaryColor, py: 0.5, borderBottom: "3px solid " + secondaryColor, display: "flex", justifyContent: "center" }}>
            <Box sx={{ height: 1.5, width: 14, bgcolor: "rgba(255,255,255,0.5)", borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "corporate-minimal":
      return (
        <Box sx={base}>
          <Box sx={{ borderRight: "3px solid " + primaryColor, pr: 0.5, mb: 0.5 }}>
            <Box sx={{ width: 18, height: 2, bgcolor: accentColor, borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "classic-simple":
      return (
        <Box sx={base}>
          <Box sx={{ borderTop: "2.5px solid " + primaryColor, borderBottom: "1px solid " + secondaryColor, py: 0.35, mb: 0.5 }} />
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ borderTop: "1px solid " + secondaryColor, py: 0.35, flexShrink: 0 }} />
        </Box>
      );
    case "classic-elegant":
      return (
        <Box sx={base}>
          <Box sx={{ borderLeft: "3px solid " + accentColor, pl: 0.5, mb: 0.5 }}>
            <Box sx={{ width: 22, height: 2, bgcolor: primaryColor, borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
        </Box>
      );
    case "invoice-minimal":
      return (
        <Box sx={base}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2.5px solid " + primaryColor, pb: 0.5, mb: 0.5, px: 0.5 }}>
            <Box sx={{ height: 2, width: 26, bgcolor: secondaryColor, borderRadius: 0.5 }} />
            <Box sx={{ height: 2, width: 12, bgcolor: accentColor, opacity: 0.7, borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, px: 0, py: 0, minHeight: 0 }}>
            <DocBodyLines showTitle />
          </Box>
          <Box sx={{ height: 3, bgcolor: primaryColor, flexShrink: 0 }} />
        </Box>
      );
    default:
      return (
        <Box sx={base}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", px: 0 }}>
            <Box sx={{ width: "70%", height: 4, borderRadius: 0.5, bgcolor: primaryColor, opacity: 0.7, mb: 0.5 }} />
            <DocBodyLines />
          </Box>
        </Box>
      );
  }
}
